/**
 * Theme Definitions for Njordheim RPG
 * Multiple color schemes for Viking/Dwarven themed game
 */

export interface Theme {
  name: string;
  displayName: string;
  premium?: boolean; // If true, only Premium+ users can select this theme
  colors: {
    // Background Colors
    background: {
      dark: string;
      medium: string;
      lightDark: string;
      card: string;
      darkTransparent: string;
      darkSemiTransparent: string;
      overlay: string; // For background image overlay
    };

    // Text Colors
    text: {
      main: string;
      light: string;
      medium: string;
      secondary: string;
      gold: string;
      gold2: string;
      highlight: string;
      muted: string;
      placeholder: string;
      legendGlow: string;
      dead: string;
    };

    // Interactive Colors
    link: {
      primary: string;
      secondary: string;
      hover: string;
    };

    // Button Colors
    button: {
      primary: string;
      primaryHover: string;
      secondary: string;
      secondaryHover: string;
      danger: string;
      dangerHover: string;
      success: string;
      warning: string;
    };

    // Border Colors
    border: {
      primary: string;
      light: string;
    };

    // Status/Role Colors
    status: {
      success: string;
      error: string;
      errorBorder: string;
      warning: string;
      info: string;
      critical: string;
      high: string;
      medium: string;
      low: string;
    };

    // Role/Badge Colors
    role: {
      guildmaster: string;
      officer: string;
      veteran: string;
      member: string;
      recruit: string;
      guildmasterBg: string;
      officerBg: string;
      memberBg: string;
    };

    // Progress Bar Colors
    progress: {
      background: string;
      fill: string;
      hp: string;
      experience: string;
      regen: string;
      barText: string;
    };

    // Special Effects
    shadow: {
      glow: string;
      dark: string;
      gold: string;
      barTextOutline: string;
    };

    // Guild/Special Colors (remain consistent across themes)
    guild: {
      background: string[];
      icon: string[];
      border: string[];
    };

    // Spinner Colors
    spinner: string;

    // Input Colors
    input: {
      background: string;
      border: string;
      focusBorder: string;
    };

    // Footer Colors
    footer: {
      background: string;
      shadow: string;
    };

    // Header Colors
    header: {
      background: string;
    };

    // Register/Auth Colors
    auth: {
      background: string;
      inputBg: string;
    };

    // Combat/Team Colors
    combat: {
      playerName: string;
      playerTeam: string;
      enemyTeam1: string;
      enemyTeam2: string;
      enemyTeam3: string;
      guildCrown: string;
    };

    // Table Colors
    table: {
      headerBg: string;
      headerText: string;
      rowBg: string;
      rowAltBg: string;
      rowHoverBg: string;
      border: string;
      text: string;
    };

    // Button Colors (general purpose)
    buttonGeneral: {
      primary: string;
      primaryHover: string;
      primaryText: string;
      secondary: string;
      secondaryHover: string;
      secondaryText: string;
      disabled: string;
      disabledText: string;
    };

    // Item Rarity Colors (consistent across themes)
    rarity: {
      common: string;
      uncommon: string;
      rare: string;
      epic: string;
      legendary: string;
    };
  };
}

// Theme 1: Frozen North - Colder, mystical, Skyrim-like
export const frozenNorthTheme: Theme = {
  name: 'frozen-north',
  displayName: 'Frozen North',
  colors: {
    background: {
      dark: '#0f172a',
      medium: '#1e293b',
      lightDark: '#334155',
      card: '#1e293b',
      darkTransparent: 'rgba(15, 23, 42, 0.95)',
      darkSemiTransparent: 'rgba(15, 23, 42, 0.7)',
      overlay: 'rgba(15, 23, 42, 0.85)',
    },
    text: {
      main: '#f8fafc',
      light: '#ffffff',
      medium: '#cbd5e1',
      secondary: '#94a3b8',
      gold: '#38bdf8',
      gold2: '#7dd3fc',
      highlight: '#bae6fd',
      muted: '#64748b',
      placeholder: '#475569',
      legendGlow: '#ffd700',
      dead: '#e11d48',
    },
    link: {
      primary: '#38bdf8',
      secondary: '#7dd3fc',
      hover: '#bae6fd',
    },
    button: {
      primary: '#0284c7',
      primaryHover: '#0369a1',
      secondary: '#334155',
      secondaryHover: '#475569',
      danger: '#8b2030',
      dangerHover: '#6d1926',
      success: '#16a34a',
      warning: '#d97706',
    },
    border: {
      primary: '#334155',
      light: '#1e293b',
    },
    status: {
      success: '#16a34a',
      error: '#e11d48',
      errorBorder: '#be123c',
      warning: '#d97706',
      info: '#0284c7',
      critical: '#9f1239',
      high: '#ea580c',
      medium: '#d97706',
      low: '#16a34a',
    },
    role: {
      guildmaster: '#38bdf8',
      officer: '#7dd3fc',
      veteran: '#cbd5e1',
      member: '#94a3b8',
      recruit: '#64748b',
      guildmasterBg: '#0f172a',
      officerBg: '#1e293b',
      memberBg: '#334155',
    },
    progress: {
      background: '#334155',
      fill: '#0284c7',
      hp: '#e11d48',
      experience: '#9333ea',
      regen: '#38bdf8',
      barText: '#ffffff',
    },
    shadow: {
      glow: 'rgba(56, 189, 248, 0.4)',
      dark: 'rgba(0, 0, 0, 0.5)',
      gold: 'rgba(56, 189, 248, 0.2)',
      barTextOutline: 'rgba(0, 0, 0, 0.7)',
    },
    guild: {
      background: ['#FF0000', '#00FF00', '#0000FF', '#FFFF00', '#FF00FF', '#00FFFF',
        '#800000', '#008000', '#000080', '#808000', '#800080', '#008080',
        '#C0C0C0', '#808080', '#000000', '#FFFFFF'],
      icon: ['#FFFFFF', '#000000', '#FFD700', '#C0C0C0', '#CD7F32', '#FF0000',
        '#00FF00', '#0000FF', '#FFFF00', '#FF00FF', '#00FFFF', '#FFA500'],
      border: ['#FFD700', '#C0C0C0', '#CD7F32', '#000000', '#FFFFFF', '#FF0000',
        '#00FF00', '#0000FF', '#800000', '#008000', '#000080', '#FFA500'],
    },
    spinner: '#38bdf8',
    input: {
      background: '#0f172a',
      border: '#334155',
      focusBorder: '#38bdf8',
    },
    footer: {
      background: '#0f172a',
      shadow: '#020617',
    },
    header: {
      background: '#0f172a',
    },
    auth: {
      background: '#1e293b',
      inputBg: '#0f172a',
    },
    combat: {
      playerName: '#16a34a',
      playerTeam: '#0284c7',
      enemyTeam1: '#e11d48',
      enemyTeam2: '#d97706',
      enemyTeam3: '#ca8a04',
      guildCrown: '#38bdf8',
    },
    table: {
      headerBg: '#0f172a',
      headerText: '#38bdf8',
      rowBg: '#1e293b',
      rowAltBg: '#0f172a',
      rowHoverBg: '#334155',
      border: '#334155',
      text: '#f8fafc',
    },
    buttonGeneral: {
      primary: '#0284c7',
      primaryHover: '#0369a1',
      primaryText: '#ffffff',
      secondary: '#334155',
      secondaryHover: '#475569',
      secondaryText: '#f8fafc',
      disabled: '#1e293b',
      disabledText: '#64748b',
    },
    rarity: {
      common: '#cccccc',
      uncommon: '#1eff00',
      rare: '#0070dd',
      epic: '#a335ee',
      legendary: '#ff8000',
    },
  },
};


// Theme 3: Dwarven Forge - Warmer, heavier, underground halls
export const dwarvenForgeTheme: Theme = {
  name: 'dwarven-forge',
  displayName: 'Dwarven Forge',
  colors: {
    background: {
      dark: '#1c1917',
      medium: '#292524',
      lightDark: '#44403c',
      card: '#292524',
      darkTransparent: 'rgba(28, 25, 23, 0.95)',
      darkSemiTransparent: 'rgba(28, 25, 23, 0.7)',
      overlay: 'rgba(28, 25, 23, 0.85)',
    },
    text: {
      main: '#fafaf9',
      light: '#ffffff',
      medium: '#d6d3d1',
      secondary: '#a8a29e',
      gold: '#f59e0b',
      gold2: '#fbbf24',
      highlight: '#fcd34d',
      muted: '#78716c',
      placeholder: '#57534e',
      legendGlow: '#ffd700',
      dead: '#b91c1c',
    },
    link: {
      primary: '#f59e0b',
      secondary: '#fbbf24',
      hover: '#fcd34d',
    },
    button: {
      primary: '#d97706',
      primaryHover: '#b45309',
      secondary: '#44403c',
      secondaryHover: '#57534e',
      danger: '#9e1c1c',
      dangerHover: '#7d1616',
      success: '#16a34a',
      warning: '#ea580c',
    },
    border: {
      primary: '#44403c',
      light: '#292524',
    },
    status: {
      success: '#16a34a',
      error: '#b91c1c',
      errorBorder: '#991616',
      warning: '#ea580c',
      info: '#f59e0b',
      critical: '#991b1b',
      high: '#ea580c',
      medium: '#f59e0b',
      low: '#16a34a',
    },
    role: {
      guildmaster: '#f59e0b',
      officer: '#fbbf24',
      veteran: '#d6d3d1',
      member: '#a8a29e',
      recruit: '#78716c',
      guildmasterBg: '#1c1917',
      officerBg: '#292524',
      memberBg: '#44403c',
    },
    progress: {
      background: '#44403c',
      fill: '#d97706',
      hp: '#dc2626',
      experience: '#9333ea',
      regen: '#ea580c',
      barText: '#ffffff',
    },
    shadow: {
      glow: 'rgba(245, 158, 11, 0.4)',
      dark: 'rgba(0, 0, 0, 0.5)',
      gold: 'rgba(245, 158, 11, 0.2)',
      barTextOutline: 'rgba(0, 0, 0, 0.7)',
    },
    guild: {
      background: ['#FF0000', '#00FF00', '#0000FF', '#FFFF00', '#FF00FF', '#00FFFF',
        '#800000', '#008000', '#000080', '#808000', '#800080', '#008080',
        '#C0C0C0', '#808080', '#000000', '#FFFFFF'],
      icon: ['#FFFFFF', '#000000', '#FFD700', '#C0C0C0', '#CD7F32', '#FF0000',
        '#00FF00', '#0000FF', '#FFFF00', '#FF00FF', '#00FFFF', '#FFA500'],
      border: ['#FFD700', '#C0C0C0', '#CD7F32', '#000000', '#FFFFFF', '#FF0000',
        '#00FF00', '#0000FF', '#800000', '#008000', '#000080', '#FFA500'],
    },
    spinner: '#f59e0b',
    input: {
      background: '#1c1917',
      border: '#44403c',
      focusBorder: '#f59e0b',
    },
    footer: {
      background: '#1c1917',
      shadow: '#0c0a09',
    },
    header: {
      background: '#1c1917',
    },
    auth: {
      background: '#292524',
      inputBg: '#1c1917',
    },
    combat: {
      playerName: '#16a34a',
      playerTeam: '#d97706',
      enemyTeam1: '#dc2626',
      enemyTeam2: '#ea580c',
      enemyTeam3: '#ca8a04',
      guildCrown: '#f59e0b',
    },
    table: {
      headerBg: '#1c1917',
      headerText: '#f59e0b',
      rowBg: '#292524',
      rowAltBg: '#1c1917',
      rowHoverBg: '#44403c',
      border: '#44403c',
      text: '#fafaf9',
    },
    buttonGeneral: {
      primary: '#d97706',
      primaryHover: '#b45309',
      primaryText: '#ffffff',
      secondary: '#44403c',
      secondaryHover: '#57534e',
      secondaryText: '#fafaf9',
      disabled: '#292524',
      disabledText: '#78716c',
    },
    rarity: {
      common: '#cccccc',
      uncommon: '#1eff00',
      rare: '#0070dd',
      epic: '#a335ee',
      legendary: '#ff8000',
    },
  },
};


// Theme 3: Dev Mode - Original color scheme
export const devModeTheme: Theme = {
  name: 'dev-mode',
  displayName: 'Dev Mode',
  colors: {
    background: {
      dark: '#09090b',
      medium: '#18181b',
      lightDark: '#27272a',
      card: '#18181b',
      darkTransparent: 'rgba(9, 9, 11, 0.95)',
      darkSemiTransparent: 'rgba(9, 9, 11, 0.7)',
      overlay: 'rgba(9, 9, 11, 0.85)',
    },
    text: {
      main: '#f4f4f5',
      light: '#ffffff',
      medium: '#d4d4d8',
      secondary: '#a1a1aa',
      gold: '#10b981',
      gold2: '#34d399',
      highlight: '#6ee7b7',
      muted: '#71717a',
      placeholder: '#52525b',
      legendGlow: '#ffd700',
      dead: '#ef4444',
    },
    link: {
      primary: '#10b981',
      secondary: '#34d399',
      hover: '#6ee7b7',
    },
    button: {
      primary: '#059669',
      primaryHover: '#047857',
      secondary: '#27272a',
      secondaryHover: '#3f3f46',
      danger: '#ef4444',
      dangerHover: '#dc2626',
      success: '#10b981',
      warning: '#f59e0b',
    },
    border: {
      primary: '#27272a',
      light: '#18181b',
    },
    status: {
      success: '#10b981',
      error: '#ef4444',
      errorBorder: '#dc2626',
      warning: '#f59e0b',
      info: '#3b82f6',
      critical: '#b91c1c',
      high: '#ea580c',
      medium: '#f59e0b',
      low: '#10b981',
    },
    role: {
      guildmaster: '#10b981',
      officer: '#34d399',
      veteran: '#d4d4d8',
      member: '#a1a1aa',
      recruit: '#71717a',
      guildmasterBg: '#09090b',
      officerBg: '#18181b',
      memberBg: '#27272a',
    },
    progress: {
      background: '#27272a',
      fill: '#059669',
      hp: '#ef4444',
      experience: '#8b5cf6',
      regen: '#10b981',
      barText: '#ffffff',
    },
    shadow: {
      glow: 'rgba(16, 185, 129, 0.4)',
      dark: 'rgba(0, 0, 0, 0.5)',
      gold: 'rgba(16, 185, 129, 0.2)',
      barTextOutline: 'rgba(0, 0, 0, 0.7)',
    },
    guild: {
      background: ['#FF0000', '#00FF00', '#0000FF', '#FFFF00', '#FF00FF', '#00FFFF',
        '#800000', '#008000', '#000080', '#808000', '#800080', '#008080',
        '#C0C0C0', '#808080', '#000000', '#FFFFFF'],
      icon: ['#FFFFFF', '#000000', '#FFD700', '#C0C0C0', '#CD7F32', '#FF0000',
        '#00FF00', '#0000FF', '#FFFF00', '#FF00FF', '#00FFFF', '#FFA500'],
      border: ['#FFD700', '#C0C0C0', '#CD7F32', '#000000', '#FFFFFF', '#FF0000',
        '#00FF00', '#0000FF', '#800000', '#008000', '#000080', '#FFA500'],
    },
    spinner: '#10b981',
    input: {
      background: '#09090b',
      border: '#27272a',
      focusBorder: '#10b981',
    },
    footer: {
      background: '#09090b',
      shadow: '#000000',
    },
    header: {
      background: '#09090b',
    },
    auth: {
      background: '#18181b',
      inputBg: '#09090b',
    },
    combat: {
      playerName: '#10b981',
      playerTeam: '#059669',
      enemyTeam1: '#ef4444',
      enemyTeam2: '#f59e0b',
      enemyTeam3: '#eab308',
      guildCrown: '#10b981',
    },
    table: {
      headerBg: '#09090b',
      headerText: '#10b981',
      rowBg: '#18181b',
      rowAltBg: '#09090b',
      rowHoverBg: '#27272a',
      border: '#27272a',
      text: '#f4f4f5',
    },
    buttonGeneral: {
      primary: '#059669',
      primaryHover: '#047857',
      primaryText: '#ffffff',
      secondary: '#27272a',
      secondaryHover: '#3f3f46',
      secondaryText: '#f4f4f5',
      disabled: '#18181b',
      disabledText: '#71717a',
    },
    rarity: {
      common: '#cccccc',
      uncommon: '#1eff00',
      rare: '#0070dd',
      epic: '#a335ee',
      legendary: '#ff8000',
    },
  },
};


// Theme 4: X26Beta - Professional admin interface
export const x26BetaTheme: Theme = {
  name: 'x26Beta',
  displayName: 'X26Beta',
  colors: {
    background: {
      dark: '#06080c',
      medium: '#0a0c10',
      lightDark: '#141c28',
      card: '#0a0c10',
      darkTransparent: 'rgba(6, 8, 12, 0.95)',
      darkSemiTransparent: 'rgba(6, 8, 12, 0.7)',
      overlay: 'rgba(6, 8, 12, 0.92)',
    },
    text: {
      main: '#e2e8f0',
      light: '#ffffff',
      medium: '#cbd5e1',
      secondary: '#94a3b8',
      gold: '#a8c7fa',
      gold2: '#78b4ff',
      highlight: '#c2d7fa',
      muted: '#64748b',
      placeholder: '#475569',
      legendGlow: '#ffd700',
      dead: '#8a2c2c',
    },
    link: {
      primary: '#a8c7fa',
      secondary: '#78b4ff',
      hover: '#c2d7fa',
    },
    button: {
      primary: '#3a5b8c',
      primaryHover: '#1e3254',
      secondary: '#141c28',
      secondaryHover: '#1e293b',
      danger: '#8b2c2c',
      dangerHover: '#a63737',
      success: '#2d5f2d',
      warning: '#b8860b',
    },
    border: {
      primary: 'rgba(120, 180, 255, 0.2)',
      light: 'rgba(120, 180, 255, 0.1)',
    },
    status: {
      success: '#228b22',
      error: '#8a2c2c',
      errorBorder: '#b94545',
      warning: '#ff8800',
      info: '#a8c7fa',
      critical: '#ff4444',
      high: '#ff8800',
      medium: '#ffaa00',
      low: '#00aa00',
    },
    role: {
      guildmaster: '#a8c7fa',
      officer: '#78b4ff',
      veteran: '#c2d7fa',
      member: '#e2e8f0',
      recruit: '#8b9bb4',
      guildmasterBg: '#0a0c10',
      officerBg: '#141c28',
      memberBg: '#1e293b',
    },
    progress: {
      background: '#141c28',
      fill: '#3a5b8c',
      hp: '#8D0A06',
      experience: '#5D2E63',
      regen: '#3a5b8c',
      barText: '#ffffff',
    },
    shadow: {
      glow: 'rgba(120, 180, 255, 0.4)',
      dark: 'rgba(0, 0, 0, 0.5)',
      gold: 'rgba(120, 180, 255, 0.2)',
      barTextOutline: 'rgba(0, 0, 0, 0.7)',
    },
    guild: {
      background: ['#FF0000', '#00FF00', '#0000FF', '#FFFF00', '#FF00FF', '#00FFFF',
        '#800000', '#008000', '#000080', '#808000', '#800080', '#008080',
        '#C0C0C0', '#808080', '#000000', '#FFFFFF'],
      icon: ['#FFFFFF', '#000000', '#FFD700', '#C0C0C0', '#CD7F32', '#FF0000',
        '#00FF00', '#0000FF', '#FFFF00', '#FF00FF', '#00FFFF', '#FFA500'],
      border: ['#FFD700', '#C0C0C0', '#CD7F32', '#000000', '#FFFFFF', '#FF0000',
        '#00FF00', '#0000FF', '#800000', '#008000', '#000080', '#FFA500'],
    },
    spinner: '#a8c7fa',
    input: {
      background: '#141c28',
      border: 'rgba(120, 180, 255, 0.2)',
      focusBorder: '#a8c7fa',
    },
    footer: {
      background: '#0a0c10',
      shadow: '#06080c',
    },
    header: {
      background: '#0a0c10',
    },
    auth: {
      background: '#0a0c10',
      inputBg: '#141c28',
    },
    combat: {
      playerName: '#228b22',
      playerTeam: '#a8c7fa',
      enemyTeam1: '#DC3545',
      enemyTeam2: '#FF8800',
      enemyTeam3: '#FFC107',
      guildCrown: '#a8c7fa',
    },
    table: {
      headerBg: 'rgba(20, 28, 40, 0.8)',
      headerText: '#a8c7fa',
      rowBg: 'rgba(10, 12, 16, 0.5)',
      rowAltBg: 'rgba(20, 28, 40, 0.3)',
      rowHoverBg: 'rgba(120, 180, 255, 0.05)',
      border: 'rgba(120, 180, 255, 0.15)',
      text: '#e2e8f0',
    },
    buttonGeneral: {
      primary: '#3a5b8c',
      primaryHover: '#1e3254',
      primaryText: '#ffffff',
      secondary: '#141c28',
      secondaryHover: '#1e293b',
      secondaryText: '#e2e8f0',
      disabled: '#141c28',
      disabledText: '#475569',
    },
    rarity: {
      common: '#cccccc',
      uncommon: '#1eff00',
      rare: '#0070dd',
      epic: '#a335ee',
      legendary: '#ff8000',
    },
  },
};

// Theme 5: Herald's Dawn - Premium light/parchment theme, illuminated manuscript aesthetic
export const heraldsDawnTheme: Theme = {
  name: 'heralds-dawn',
  displayName: "Herald's Dawn",
  premium: true,
  colors: {
    background: {
      dark: '#fdf6e4',
      medium: '#f5ead0',
      lightDark: '#e8d5b0',
      card: '#fdf6e4',
      darkTransparent: 'rgba(253, 246, 228, 0.97)',
      darkSemiTransparent: 'rgba(253, 246, 228, 0.8)',
      overlay: 'rgba(253, 246, 228, 0.95)',
    },
    text: {
      main: '#2c1a0e',
      light: '#1a0f07',
      medium: '#4a2c17',
      secondary: '#6b3d22',
      gold: '#8b4513',
      gold2: '#a0522d',
      highlight: '#5d2a0c',
      muted: '#9c7a5c',
      placeholder: '#b89474',
      legendGlow: '#b8860b',
      dead: '#c0392b',
    },
    link: {
      primary: '#8b4513',
      secondary: '#a0522d',
      hover: '#5d2a0c',
    },
    button: {
      primary: '#5d4037',
      primaryHover: '#7a5648',
      secondary: '#e8d5b0',
      secondaryHover: '#d9c090',
      danger: '#c0392b',
      dangerHover: '#96281b',
      success: '#2e7d32',
      warning: '#e65100',
    },
    border: {
      primary: 'rgba(139, 69, 19, 0.35)',
      light: 'rgba(139, 69, 19, 0.18)',
    },
    status: {
      success: '#2e7d32',
      error: '#c0392b',
      errorBorder: '#96281b',
      warning: '#e65100',
      info: '#1565c0',
      critical: '#7b1818',
      high: '#bf360c',
      medium: '#e65100',
      low: '#2e7d32',
    },
    role: {
      guildmaster: '#8b4513',
      officer: '#a0522d',
      veteran: '#4a2c17',
      member: '#6b3d22',
      recruit: '#9c7a5c',
      guildmasterBg: '#fdf6e4',
      officerBg: '#f5ead0',
      memberBg: '#e8d5b0',
    },
    progress: {
      background: '#e8d5b0',
      fill: '#5d4037',
      hp: '#c0392b',
      experience: '#6a1e9a',
      regen: '#1565c0',
      barText: '#ffffff',
    },
    shadow: {
      glow: 'rgba(139, 69, 19, 0.3)',
      dark: 'rgba(44, 26, 14, 0.25)',
      gold: 'rgba(139, 69, 19, 0.18)',
      barTextOutline: 'rgba(44, 26, 14, 0.75)',
    },
    guild: {
      background: ['#FF0000', '#00FF00', '#0000FF', '#FFFF00', '#FF00FF', '#00FFFF',
        '#800000', '#008000', '#000080', '#808000', '#800080', '#008080',
        '#C0C0C0', '#808080', '#000000', '#FFFFFF'],
      icon: ['#FFFFFF', '#000000', '#FFD700', '#C0C0C0', '#CD7F32', '#FF0000',
        '#00FF00', '#0000FF', '#FFFF00', '#FF00FF', '#00FFFF', '#FFA500'],
      border: ['#FFD700', '#C0C0C0', '#CD7F32', '#000000', '#FFFFFF', '#FF0000',
        '#00FF00', '#0000FF', '#800000', '#008000', '#000080', '#FFA500'],
    },
    spinner: '#8b4513',
    input: {
      background: '#f5ead0',
      border: 'rgba(139, 69, 19, 0.35)',
      focusBorder: '#8b4513',
    },
    footer: {
      background: '#e8d5b0',
      shadow: '#d9c090',
    },
    header: {
      background: '#e8d5b0',
    },
    auth: {
      background: '#f5ead0',
      inputBg: '#fdf6e4',
    },
    combat: {
      playerName: '#2e7d32',
      playerTeam: '#1565c0',
      enemyTeam1: '#c0392b',
      enemyTeam2: '#e65100',
      enemyTeam3: '#f9a825',
      guildCrown: '#8b4513',
    },
    table: {
      headerBg: '#e8d5b0',
      headerText: '#5d2a0c',
      rowBg: '#fdf6e4',
      rowAltBg: '#f5ead0',
      rowHoverBg: 'rgba(139, 69, 19, 0.07)',
      border: 'rgba(139, 69, 19, 0.22)',
      text: '#2c1a0e',
    },
    buttonGeneral: {
      primary: '#5d4037',
      primaryHover: '#7a5648',
      primaryText: '#fdf6e4',
      secondary: '#e8d5b0',
      secondaryHover: '#d9c090',
      secondaryText: '#2c1a0e',
      disabled: '#f5ead0',
      disabledText: '#9c7a5c',
    },
    rarity: {
      common: '#cccccc',
      uncommon: '#1eff00',
      rare: '#0070dd',
      epic: '#a335ee',
      legendary: '#ff8000',
    },
  },
};

// All available themes
export const themes: Theme[] = [
  frozenNorthTheme,
  dwarvenForgeTheme,
  devModeTheme,
  x26BetaTheme,
  heraldsDawnTheme,
];

// Default theme
export const defaultTheme = x26BetaTheme;

// Helper function to get theme by name
export function getThemeByName(name: string): Theme {
  return themes.find(t => t.name === name) || defaultTheme;
}
